import ApplicationController from "./application_controller";
import ApiStore from "./folders/api_store";
import InputStore from "./folders/input_store";

export default class extends ApplicationController {
  static values = { apiBaseUrl: String };
  static classes = ["droppable", "dropOver"];
  static targets = ["input", "tree", "folder"];
  static outlets = ["document-list", "folders--menu", "edit-inline"];

  connect() {
    if (this.hasInputTarget) {
      this.store = new InputStore(this.inputTarget);
    } else if (this.hasApiBaseUrlValue) {
      this.store = new ApiStore(this.apiBaseUrlValue);
    } else {
      return;
    }
    this.insertContextMenuTemplate();
    this.initialiseTreeTable();
    this.initFolderAutoScroll();
    this.connectStickyPanel();
  }

  disconnect() {
    if (this._folderScrollCleanup) this._folderScrollCleanup();
    this.disconnectStickyPanel();
  }

  connectStickyPanel() {
    // Architecture: why position:relative + top on #sidepanel
    //
    // The app runs inside an <iframe>. The parent page scrolls; the iframe has
    // NO internal scroll (window.pageYOffset === 0 always inside the iframe).
    //
    // Goal: keep #sidepanel visually stuck to the top of the visible iframe area
    // as the user scrolls the parent page.
    //
    // Approach — position:relative; top on #sidepanel:
    //   On every parent scroll event we set:
    //     top = parentScroll − naturalTopInIframe
    //   This shifts #sidepanel down the iframe document at exactly the same rate
    //   the parent scrolls up, so from the parent viewport the panel appears
    //   stationary — sticky. Visual result is identical to translateY(same value).
    //
    //   Why NOT transform:translateY (previous approach)?
    //     Safari (WebKit) has a long-standing bug: getBoundingClientRect() on
    //     elements INSIDE a CSS-transformed ancestor returns coordinates relative
    //     to the pre-transform position, not the actual viewport position.
    //     jQuery UI .droppable() caches folder-row positions via getBCR() in
    //     $.ui.ddmanager.prepareOffsets(). With transform on #sidepanel, Safari
    //     reports droppable rows at their original (un-shifted) Y, while the drag
    //     helper moves at the correct visual Y. The positions diverge by the
    //     translateY amount — intersection never fires, no hover highlight, no drop.
    //     position:relative is a layout-model property; getBCR() correctly reflects
    //     the shifted position in all browsers including Safari. No stacking context
    //     is created, so jQuery UI hit-detection works correctly.
    //
    //   Why NOT position:fixed?
    //     position:fixed; top: topInIframe resolves to Y=0 in the parent viewport
    //     (behind the fixed app header), because:
    //       (iframeDocY − parentScroll) + (parentScroll − iframeDocY) = 0
    //     There is no correctable value — the required top: parentScroll would
    //     overflow the iframe element height.
    //
    //   Why NOT position:sticky?
    //     Sticks relative to the nearest scrolling ancestor. The iframe has no
    //     internal scroll — sticky has nothing to stick to.
    //
    //   Scroll-loop prevention — why we lock #sidepanel.style.height:
    //     position:relative; top: X does NOT change the layout footprint of
    //     #sidepanel (its flow box stays at the original position). However,
    //     resizing .scroll-style height each tick changes #sidepanel's rendered
    //     height. To prevent that from shrinking #toplevelDropZone's layout,
    //     triggering a scroll bounce loop, we lock height explicitly on first
    //     activation.
    //
    //   Why clip-path for bottom-clipping?
    //     clip-path: inset() clips painted output only — zero layout impact.
    //     overflow:hidden on a relatively-positioned element can be unreliable
    //     when combined with dynamic height changes. max-height would change
    //     layout footprint → scroll loop.
    //
    //   Coordinate space — why topInIframe before the condition:
    //     parentScroll is in parent-document pixels.
    //     _naturalTopInIframe is #sidepanel's Y in the iframe document
    //       (= iframe-viewport Y since the iframe has no internal scroll).
    //     Direct comparison (parentScroll > _naturalTopInIframe) mixes spaces —
    //     it activates too early when the iframe has a vertical offset.
    //     topInIframe = parentScroll − _stickyTopInViewport brings both sides
    //     into iframe-document coordinates for a correct comparison.

    const sidepanel = document.getElementById("sidepanel");
    const topZone   = document.getElementById("toplevelDropZone");
    if (!sidepanel) return;

    // All instance state is nulled here so stale values from a previous sticky
    // session (e.g. after the panel was hidden and re-shown) never pollute the next.
    this._naturalTopInIframe  = null; // #sidepanel's Y in the iframe document (constant)
    this._stickyTopInViewport = null; // iframe element's Y in the parent document (constant)
    this._naturalHeight       = null; // #sidepanel's layout height — locked for the scroll-loop guard
    this._scrollOffset        = 0;   // px gap: #sidepanel top → .scroll-style top (header content)
    this._scrollContainer     = null; // cached ref to .scroll-style — avoids per-tick DOM queries
    this._isFixed             = false; // true once the one-time activation setup has run

    // Restore #sidepanel to its natural in-flow state and clear all sticky styles.
    // Called when the user scrolls back above the activation threshold, or when
    // the MutationObserver detects the panel has been hidden and then re-shown.
    this._applyRelative = () => {
      // Reset position:relative + top (the sticky shift mechanism).
      // Clearing these inline styles reverts #sidepanel to its CSS-defined position.
      sidepanel.style.position       = "";
      sidepanel.style.top            = "";
      sidepanel.style.height         = ""; // release the scroll-loop guard height lock
      sidepanel.style.clipPath       = "";
      sidepanel.style.webkitClipPath = "";
      if (this._scrollContainer) this._scrollContainer.style.height = "";
      // Reset all snapshots so the next sticky activation re-measures from scratch.
      // Required because layout may have changed while the panel was non-sticky.
      this._naturalTopInIframe  = null;
      this._stickyTopInViewport = null;
      this._naturalHeight       = null;
      this._scrollOffset        = 0;
      this._scrollContainer     = null;
      this._isFixed             = false;
    };

    this._stickyHandler = () => {
      if (sidepanel.classList.contains("d-none")) return; // panel hidden — nothing to do

      // Snapshot natural geometry (once, before any top offset is applied)
      // Measurements taken while #sidepanel is still in its natural CSS position
      // so getBCR() reflects true iframe-document coordinates.
      // Since the iframe has no internal scroll, getBCR().top = element's iframe-doc Y.
      if (this._naturalTopInIframe === null) {
        this._naturalTopInIframe = sidepanel.getBoundingClientRect().top;

        // _stickyTopInViewport: iframe element's fixed Y in the parent document.
        //   = frameElement.getBCR().top (iframe-in-parent-viewport now)
        //   + window.parent.pageYOffset (current parent scroll)
        //   = a constant regardless of future scroll, used to convert parentScroll
        //     into iframe-document coordinates for the activation comparison.
        try {
          this._stickyTopInViewport = window.frameElement
            ? window.frameElement.getBoundingClientRect().top + window.parent.pageYOffset
            : 0;
        } catch (e) { this._stickyTopInViewport = 0; }
      }

      try {
        const parentScroll = window.parent.pageYOffset;

        // topInIframe: parentScroll converted to iframe-document coordinates.
        // Represents how many iframe-document pixels are above the visible viewport edge.
        // Same coordinate space as _naturalTopInIframe — correct comparison.
        const topInIframe = parentScroll - this._stickyTopInViewport;

        // Activate sticky when the visible iframe area has scrolled past
        // #sidepanel's natural in-iframe position.
        // Both sides are now in iframe-document coordinates — correct comparison.
        if (topInIframe > this._naturalTopInIframe) {

          if (!this._isFixed) {
            // One-time setup on first sticky activation
            this._isFixed = true;

            // Lock #sidepanel layout height to prevent .scroll-style height changes
            // from shrinking #sidepanel's flow footprint and triggering a scroll loop.
            this._naturalHeight = sidepanel.offsetHeight;
            sidepanel.style.height = this._naturalHeight + "px";

            // Cache .scroll-style and the vertical gap above it within #sidepanel
            // (heading, controls). Snapshotted once — stable while sticky is active.
            // Cached to avoid forced synchronous reflows on every scroll tick.
            this._scrollContainer = sidepanel.querySelector(".scroll-style");
            if (this._scrollContainer) {
              this._scrollOffset = Math.max(
                0,
                this._scrollContainer.getBoundingClientRect().top -
                sidepanel.getBoundingClientRect().top
              );
            }
          }

          // Per-tick updates

          // Available height this tick, clamped to #toplevelDropZone boundary
          // when the zone end is genuinely visible within the panel's extent.
          const zoneBottom    = topZone ? topZone.getBoundingClientRect().bottom : Infinity;
          const maxByViewport = window.parent.innerHeight - this._stickyTopInViewport;
          const available     = (zoneBottom < parentScroll + maxByViewport)
            ? Math.max(50, zoneBottom - parentScroll)
            : maxByViewport;

          // Resize .scroll-style to fill the visible panel height.
          // Safe: #sidepanel.style.height is locked, so this does not affect
          // page layout or scroll geometry.
          if (this._scrollContainer) {
            this._scrollContainer.style.height =
              Math.max(50, available - this._scrollOffset) + "px";
          }

          // Clip the visual bottom of #sidepanel.
          // clip-path clips painted output only — zero layout impact.
          // -webkit- prefix for Safari < 13.1.
          const clipBottom = Math.max(0, this._naturalHeight - available);
          sidepanel.style.webkitClipPath = `inset(0px 0px ${clipBottom}px 0px)`;
          sidepanel.style.clipPath       = `inset(0px 0px ${clipBottom}px 0px)`;

          // Apply the sticky shift via position:relative + top.
          //
          // top = parentScroll − naturalTopInIframe
          //
          // This keeps the panel's visual Y in the parent viewport at iframeDocY
          // (the iframe element's fixed Y in the parent document), which is
          // right below any fixed parent-page header — correct in all browsers.
          //
          // Critically: position:relative does NOT create a new stacking context
          // and does NOT affect Safari's getBoundingClientRect() behaviour.
          // jQuery UI's droppable hit-detection (which caches positions via getBCR)
          // therefore returns the correct shifted coordinates for all folder rows
          // in all browsers — including Safari where transform would have caused
          // getBCR() to return pre-shift positions, breaking hover and drop.
          sidepanel.style.position = "relative";
          sidepanel.style.top      = (parentScroll - this._naturalTopInIframe) + "px";

        } else {
          // Parent scrolled back above the activation threshold — remove sticky.
          this._applyRelative();
        }
      } catch (e) { /* cross-origin guard: window.parent access throws in cross-origin iframes */ }
    };

    // Listen on the PARENT page's scroll event (the iframe has no scroll of its own).
    // Stored on `this` so disconnectStickyPanel() can pass the exact same function
    // reference to removeEventListener — required for correct removal.
    try { window.parent.addEventListener("scroll", this._stickyHandler); } catch (e) {}

    // Watch for d-none class changes on #sidepanel (panel shown or hidden by other code).
    //
    // When the panel re-appears after being hidden:
    //   _applyRelative() clears all stale snapshot values (geometry may have changed
    //   while the panel was hidden, e.g. due to window resize or DOM updates).
    //   _stickyHandler() then immediately re-evaluates for the current scroll position
    //   so the panel is correctly sticky or natural from the first visible frame.
    //
    // attributeFilter limits observation to class attribute mutations only,
    // preventing unnecessary callbacks for unrelated attribute changes.
    this._sidepanelObserver = new MutationObserver(() => {
      if (!sidepanel.classList.contains("d-none")) {
        this._applyRelative(); // clear stale snapshots
        this._stickyHandler(); // re-evaluate immediately
      }
    });
    this._sidepanelObserver.observe(sidepanel, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  disconnectStickyPanel() {
    // Disconnect the MutationObserver first to prevent it firing on a detached
    // or partially-torn-down DOM node after the Stimulus controller is destroyed.
    if (this._sidepanelObserver) {
      this._sidepanelObserver.disconnect();
      this._sidepanelObserver = null;
    }

    // Remove the parent scroll listener.
    // Critical on Turbo / Stimulus reconnects: without removal, stale listeners
    // accumulate across page navigations, causing ghost scroll behaviour and
    // potential memory leaks. Must pass the exact same function reference that
    // was registered in connectStickyPanel().
    if (this._stickyHandler) {
      try { window.parent.removeEventListener("scroll", this._stickyHandler); } catch (e) {}
      this._stickyHandler = null;
    }

    // Reset all inline styles so #sidepanel and .scroll-style return to their
    // CSS-defined defaults (no leftover transform, height lock, or clip).
    const sidepanel = document.getElementById("sidepanel");
    if (sidepanel) {
      sidepanel.style.position       = ""; // revert sticky shift
      sidepanel.style.top            = "";
      sidepanel.style.height         = ""; // release height lock
      sidepanel.style.clipPath       = "";
      sidepanel.style.webkitClipPath = "";
      const sc = sidepanel.querySelector(".scroll-style");
      if (sc) sc.style.height = "";
    }
  }

  initFolderAutoScroll() {
    // Why a RAF loop rather than scrolling directly in dragover
    // dragover fires at the browser's raw event rate — potentially hundreds of
    // times per second in Chrome. Calling window.parent.scrollBy() on every event
    // causes visible jank and makes it impossible to aim at a specific folder row.
    // The RAF loop decouples output from input: dragover only writes cursorY (one
    // integer), and the RAF reads it once per animation frame (~60fps max).
    // Result: smooth, jank-free scrolling at a predictable rate regardless of
    // how fast the browser fires dragover.

    // Why acceleration/deceleration (ACCEL/DECEL)
    // Fixed scroll speed is either too slow (user waits) or too fast (overshoots
    // the target folder). Ramping up when entering a zone and decelerating when
    // leaving gives the user fine-grained control:
    //   ACCEL=1.5 px/frame² → gentle ramp, feels natural entering the zone.
    //   DECEL=3 px/frame²   → stops in ~4 frames (~67ms) — fast enough to aim
    //                          precisely at any visible folder row for a drop.
    // No hover timers or jitter thresholds needed — the neutral-band deceleration
    // is the only stop mechanism, and it's immune to Chrome's sub-pixel dragover
    // jitter (tiny cursor movements that would reset a position-delta timer).

    // Why the hasMoved (5px) guard
    // jQuery UI .draggable() fires 'dragstart' on mousedown even if the user
    // hasn't moved — a plain click-hold triggers it. Without the guard the RAF
    // loop starts on every click, potentially scrolling the page unexpectedly.
    // 5px matches typical cursor micro-movement during a click-hold.

    // Why document-level listeners, not container listeners
    // During drag the cursor frequently leaves the .scroll-style container (e.g.
    // slow upward drag triggers parent-page scroll, moving the container above
    // the cursor). Container-level dragover stops firing the moment the cursor
    // leaves the boundary. document-level listeners fire unconditionally for the
    // duration of the drag.

    // Firefox scrollY === 0 guard
    // In Firefox, calling window.parent.scrollBy(0, negativeValue) when scrollY
    // is already 0 fires a spurious 'dragend' event on the jQuery UI draggable,
    // terminating the drag immediately and confusingly for the user.
    // The guard prevents calling scrollBy in this specific state.
    // No equivalent guard is needed for downward scroll — scrollBy at the page
    // bottom is a silent no-op in all browsers.

    // Dual event binding: native + jQuery UI
    // document.addEventListener("dragstart") catches HTML5 native drag events
    // (fired when an element has draggable="true" and the user starts a drag).
    // $(document).on("dragstart.folderAutoScroll") catches jQuery UI drag events
    // (.draggable() uses mousedown/mousemove internally and does NOT fire the
    // native HTML5 dragstart — it fires its own jQuery event with the same name).
    // Both bindings are needed because the app uses jQuery UI .draggable() for
    // folder-to-folder moves and potentially HTML5 drag for document-to-folder drops.

    // Find the scrollable folder panel. Nothing to do if it doesn't exist.
    const container = this.element.querySelector(".scroll-style");
    if (!container) return;

    // State
    let dragActive = false;  // true while a drag operation is in progress
    let hasMoved   = false;  // true once cursor has moved >5px from drag-start
    let startY     = -1;     // parent-viewport Y where the drag began (for hasMoved check only)
    let cursorY    = -1;     // latest parent-viewport Y from dragover (read by RAF each frame)
    let scrollVelocity = 0;  // current scroll speed in px/frame, signed: negative=up, positive=down
    let rafId      = null;      // handle for the active requestAnimationFrame loop

    // Separate flag: true when drag was triggered by jQuery UI .draggable(),
    // false when triggered by native HTML5 drag (document-to-folder).
    // jQuery UI drag uses mouse events internally and does NOT fire the native
    // HTML5 dragstart event — only the jQuery custom event fires.
    // We use this to skip window.parent.scrollBy() for jQuery UI drags:
    // in Safari, event.pageY inside an iframe includes the parent page's scroll
    // offset. Calling scrollBy() mid-drag changes parentScroll, which shifts
    // pageY on every subsequent mousemove event, corrupting jQuery UI's
    // _generatePosition() — the helper drifts and misses the droppable.
    let isJQueryUIDrag = false;

    // Tuning constants
    const ZONE_PX   = 80;   // px from the screen edge that activates scrolling.
    // Middle band (ZONE_PX ... vh-ZONE_PX) is the neutral/drop zone

    const MIN_SPEED = 2;    // px/frame at the zone boundary (gentle start)
    const MAX_SPEED = 12;   // px/frame at the screen edge (full speed)
    const ACCEL     = 1.5;  // px/frame^2 — ramp-up rate when entering a scroll zone
    const DECEL     = 3;    // px/frame^2 — ramp-down rate when entering the neutral zone
    // At MAX_SPEED=12 and DECEL=3: stops in 4 frames (~67ms) which is fast enough to aim at a specific folder for drop.

    // Coordinate helper
    // The app runs inside an iframe. e.clientY is relative to the iframe's own
    // top-left corner, which shifts every time the parent page scrolls (because
    // the iframe element moves up/down in the parent viewport).
    //
    // We need Y coordinate that is stable across parent-page scrolling —
    // i.e. always relative to the physical screen edge, not the iframe edge.
    //
    // window.frameElement.getBoundingClientRect().top gives the iframe's current
    // offset from the parent viewport top. Adding it to e.clientY cancels the
    // iframe-relative component and yields the cursor's parent-viewport Y.
    //
    // Example: iframe top is at parent-viewport y=-400 (scrolled down),
    //          e.clientY=900 → parentY = 900 + (-400) = 500. Cursor is at
    //          screen y=500, which is correct and stays 500 as the page scrolls.
    //
    // The try/catch guards against cross-origin iframes (frameElement would throw).
    // In that case we fall back to raw clientY — zones shift slightly but still work.
    const toParentY = (clientY) => {
      try {
        if (window.frameElement) {
          return clientY + window.frameElement.getBoundingClientRect().top;
        }
      } catch (e) { /* cross-origin: fall back to iframe-relative coordinate */ }
      return clientY;
    };

    // RAF scroll loop
    // Runs at ~60fps while a drag is active. Reads cursorY (set by dragover)
    // and decides how fast to scroll this frame. All scroll I/O happens here -
    // dragover only updates cursorY, never scrolls directly.
    const scrollStep = () => {
      if (!dragActive) { rafId = null; return; }

      if (hasMoved && cursorY >= 0) {
        const vh = window.parent ? window.parent.innerHeight : window.innerHeight;
        // vh is read fresh every frame so a window resize mid-drag is handled correctly.

        // Determine the target speed based on which zone the cursor is in.
        // Speed is proportional to how deep into the zone the cursor is:
        //   depth=0 at the zone boundary - MIN_SPEED
        //   depth=ZONE_PX at the screen edge - MAX_SPEED
        let targetSpeed = 0;

        if (cursorY < ZONE_PX) {
          // TOP ZONE: cursor is in the top 80px of the screen - scroll UP
          const depth = ZONE_PX - cursorY;
          targetSpeed = -(MIN_SPEED + (MAX_SPEED - MIN_SPEED) * depth / ZONE_PX);

        } else if (cursorY > vh - ZONE_PX) {
          // BOTTOM ZONE: cursor is in the bottom 80px of the screen - scroll DOWN
          const depth = cursorY - (vh - ZONE_PX);
          targetSpeed =  (MIN_SPEED + (MAX_SPEED - MIN_SPEED) * depth / ZONE_PX);

        }
        // NEUTRAL ZONE: cursor is in the middle band - targetSpeed stays 0
        // Velocity decelerates to 0 in the else-branch below.
        // This is the only stop mechanism needed — no hover timers, no jitter
        // thresholds. Any cursor position in the middle band stops scroll cleanly,
        // immune to Chrome's sub-pixel dragover jitter.

        // Smoothly move scrollVelocity toward targetSpeed each frame.
        // This produces gradual acceleration when entering a zone and a
        // smooth ~67ms stop when the cursor moves into the neutral zone.
        if (targetSpeed < 0) {
          // Accelerating upward: don't overshoot targetSpeed
          scrollVelocity = Math.max(targetSpeed, scrollVelocity - ACCEL);
        } else if (targetSpeed > 0) {
          // Accelerating downward: don't overshoot targetSpeed
          scrollVelocity = Math.min(targetSpeed, scrollVelocity + ACCEL);
        } else {
          // Neutral zone: decelerate toward zero
          if      (scrollVelocity > 0) scrollVelocity = Math.max(0, scrollVelocity - DECEL);
          else if (scrollVelocity < 0) scrollVelocity = Math.min(0, scrollVelocity + DECEL);
        }

        if (!isJQueryUIDrag) {
          if (scrollVelocity !== 0) {
            // Scroll the folder panel's internal content (shows different folders).
            container.scrollTop += scrollVelocity;

            // Also scroll the parent page so the folder panel itself stays in view.
            // The sign of scrollVelocity already encodes direction (negative = up).
            if (scrollVelocity < 0) {
              // Scrolling UP: guard against calling scrollBy when already at the
              // top of the page. In Firefox, calling window.parent.scrollBy(0, negative)
              // when scrollY===0 fires a spurious 'dragend' event, which would
              // immediately kill the drag operation.
              if (window.parent.scrollY > 0) window.parent.scrollBy(0, scrollVelocity);
            } else {
              window.parent.scrollBy(0, scrollVelocity);
            }
          }
        }
      }

      rafId = requestAnimationFrame(scrollStep);
    };

    // Cursor tracking
    // Called on every dragover event. Converts the iframe-relative clientY to
    // a stable parent-viewport Y and stores it for the RAF loop to consume.
    const updateCursor = (clientY) => {
      const y = toParentY(clientY);

      // Capture drag-start position for the hasMoved guard.
      if (startY === -1) { startY = y; }
        // Only activate scrolling after the cursor has genuinely moved, not on a plain click that happens to fire dragstart.
      else if (!hasMoved && Math.abs(y - startY) > 5) { hasMoved = true; }

      cursorY = y; // RAF loop reads this value; safe to update at dragover rate
    };

    // Native drag event listeners
    // preventDefault() on dragenter and dragover is required for the 'drop'
    // event to fire on the target element (browser default is to forbid drops).
    const onDocDragEnter = (e) => { e.preventDefault(); };
    const onDocDragOver  = (e) => { e.preventDefault(); updateCursor(e.clientY); };
    const onDocDrop      = (e) => { e.preventDefault(); };

    const onDragStart = () => {
      isJQueryUIDrag = false; // default: assume HTML5 drag; jQuery UI handler overrides below
      dragActive     = true;
      hasMoved       = false;
      startY         = -1;
      cursorY        = -1;
      scrollVelocity = 0;
      container.classList.add("drag-active");
      // Start the RAF loop. Guard against double-starting if somehow called twice.
      if (!rafId) rafId = requestAnimationFrame(scrollStep);
    };

    // Separate handler for jQuery UI drag start — sets the flag THEN delegates.
    const onJQueryUIDragStart = () => {
      isJQueryUIDrag = true;
      onDragStart();
    };

    const onDragEnd = () => {
      // Reset all state so a subsequent drag starts clean.
      dragActive = false;
      hasMoved   = false;
      startY     = -1;
      cursorY    = -1;
      scrollVelocity = 0;
      container.classList.remove("drag-active");
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    };

    // Register listeners
    // Listeners are on `document` (not the container) so they fire regardless
    // of which element the cursor is over during the drag.
    //
    // Native HTML5 events cover document drag (the primary use case).
    // jQuery UI events (dragstart/dragstop namespaced) cover folder dragging
    // via jQuery UI .draggable(), which does not fire native HTML5 drag events.
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("dragend",   onDragEnd);
    document.addEventListener("dragenter", onDocDragEnter);
    document.addEventListener("dragover",  onDocDragOver);
    document.addEventListener("drop",      onDocDrop);
    $(document).on("dragstart.folderAutoScroll", onJQueryUIDragStart);
    $(document).on("dragstop.folderAutoScroll",  onDragEnd);

    // Cleanup
    // Called by the Stimulus disconnect lifecycle when the controller is turn
    // down (e.g. page navigation or component removal). Removes every listener
    // added above and cancels any in-flight RAF to prevent memory leaks and
    // ghost scroll behavior after the component is gone.
    this._folderScrollCleanup = () => {
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("dragend",   onDragEnd);
      document.removeEventListener("dragenter", onDocDragEnter);
      document.removeEventListener("dragover",  onDocDragOver);
      document.removeEventListener("drop",      onDocDrop);
      $(document).off(".folderAutoScroll");
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    };
  }

  insertContextMenuTemplate() {
    const foldersMenuTemplate = document.getElementById("folder-menu-tpl");
    if (foldersMenuTemplate) {
      document
        .getElementById("folder-menu")
        .replaceWith(foldersMenuTemplate.content.cloneNode(true));
    }
  }

  async initialiseTreeTable() {
    const data = await this.store.folders();
    const treeRows = this.nodeToRows(data.tree);
    this.treeTarget.replaceChildren(...treeRows);
    $(this.treeTarget).treetable({
      expandable: true,
      initialState: "expanded",
      indent: 10,
    });
    if (this.deferredFolderSelection) {
      this.selectFolder(this.deferredFolderSelection);
      delete this.deferredFolderSelection;
    }
    this.makeTreeNodesDraggable();
  }

  nodeToRows(node, parentId) {
    return [this.nodeToRow(node, parentId)].concat(
      node.children.map((item) => this.nodeToRows(item, node.id)).flat(),
    );
  }

  nodeToRow(node, parentId) {
    const root = parentId === undefined;
    const fragment = document
      .getElementById(this.rowTemplate(this.hasDocumentListOutlet))
      .content.cloneNode(true);
    const row = fragment.firstElementChild;
    row.dataset.ttId = node.id;
    row.dataset.foldersFolderIdParam = node.id;
    if (row.dataset.documentListFolderIdParam !== undefined) {
      row.dataset.documentListFolderIdParam = node.id;
    }
    if (!root) {
      row.dataset.ttParentId = parentId;
    }
    const clickableSpan = row.querySelector("td > span");
    if (clickableSpan) {
      clickableSpan.dataset.foldersFolderIdParam = node.id;
    }
    const folderNameElement = row.querySelector(".folders-row__folder-name");
    if (folderNameElement) {
      folderNameElement.textContent = node.name;
    }
    return fragment;
  }

  rowTemplate(documentList) {
    return (documentList ? "document-list-" : "") + "folder-row-tpl";
  }

  select(e) {
    this.selectedFolderId = e.params.folderId;
    this.selectFolder(this.selectedFolderId);
  }

  contextMenu(e) {
    this.selectedFolderId = e.params.folderId;
    this.selectFolder(this.selectedFolderId);
    this.foldersMenuOutlet.selectedFolder(this.selectedFolderId);
    this.foldersMenuOutlet.showNearEventPoint(e, e.timeStamp);
    e.preventDefault();
  }

  async add(e) {
    const unsavedFolder = {
      id: "new",
      name: "New folder",
      children: [],
    };
    const unsavedFolderRow = this.nodeToRow(
      unsavedFolder,
      this.selectedFolderId,
    );
    const $tree = $(this.treeTarget);
    const parentNode = $tree.treetable("node", this.selectedFolderId);
    $tree.treetable(
      "loadBranch",
      parentNode,
      unsavedFolderRow.firstElementChild.outerHTML,
    );
    this.selectedFolderId = unsavedFolder.id;
    this.rename();
  }

  rename() {
    const folderElement = this.folderElement(this.selectedFolderId);
    const editController = this.editInlineOutlets.find((controller) =>
      folderElement.contains(controller.element),
    );
    editController.edit();
  }

  async renameComplete({ detail: { value } }) {
    if (this.selectedFolderId === "new") {
      const folderElement = this.folderElement(this.selectedFolderId);
      const parentFolderId = folderElement.dataset.ttParentId;
      const newFolder = await this.store.create(parentFolderId, value);
      const newFolderRow = this.nodeToRow(newFolder, parentFolderId);
      const $tree = $(this.treeTarget);
      const parentNode = $tree.treetable("node", parentFolderId);
      $tree.treetable("removeNode", this.selectedFolderId);
      $tree.treetable(
        "loadBranch",
        parentNode,
        newFolderRow.firstElementChild.outerHTML,
      );
      this.selectedFolderId = newFolder.id;

      // add event handlers for new node
      this.makeTreeNodesDraggable();
    } else {
      await this.store.rename(this.selectedFolderId, value);
      this.folderElement(this.selectedFolderId).querySelector(
        ".folders-row__folder-name",
      ).innerText = value;
    }
    this.selectFolder(this.selectedFolderId);
  }

  async delete() {
    await this.store.delete(this.selectedFolderId);
    this.selectedFolderId = this.removeFromTree(this.selectedFolderId);
    this.selectFolder(this.selectedFolderId);
  }

  removeFromTree(folderId) {
    const selectedRow = this.folderElement(this.selectedFolderId);
    const parentFolderId = selectedRow.dataset.ttParentId;
    const $tree = $(this.treeTarget);
    $tree.treetable("removeNode", this.selectedFolderId);
    this.removeFolderIconIfEmpty(parentFolderId);
    return parentFolderId;
  }

  selectFolder(folderId) {
    if (this.hasFolderTarget) {
      this.folderTargets.forEach((element) => {
        element.classList.toggle("selected", element.dataset.ttId == folderId);
      });
      if (folderId != null) {
        $(this.treeTarget).treetable("reveal", folderId);
        this.dispatch("select", { detail: { folderId } });
      }
    } else {
      this.deferredFolderSelection = folderId;
    }
  }

  selectNone() {
    this.selectedFolderId = null;
    this.selectFolder();
  }

  folderElement(folderId) {
    return this.folderTargets.find(
      (element) => element.dataset.ttId == folderId,
    );
  }

  makeTreeNodesDraggable() {
    $(this.treeTarget)
      .find("tr")
      .draggable({
        helper: "clone",
        opacity: 0.75,
        refreshPositions: true,
        revert: "invalid",
        revertDuration: 300,
        scroll: true,
      })
      .droppable({
        hoverClass: "accept",
        accept: "tr[data-tt-id]",
        over: this.dropOver.bind(this),
        drop: this.dropped.bind(this),
      });
  }

  dropOver(e) {
    const target = e.target;
    if (!target.classList.contains("expanded")) {
      $(this.treeTarget).treetable("expandNode", target.dataset.ttId);
    }
  }

  async dropped(e, ui) {
    const draggableFolderId = ui.draggable[0].dataset.ttId;
    const newParentId = e.target.dataset.ttId;

    await this.store.move(draggableFolderId, newParentId);
    this.moveTreeRows(draggableFolderId, newParentId);
  }

  // treetable's "move" operation inserts as first child rather than
  // last. Use "loadBranch" to insert as last child, replicating semantics
  // of our data store implementations.
  moveTreeRows(folderId, newParentId) {
    const folderRow = this.folderElement(folderId);
    const parentFolderId = folderRow.dataset.ttParentId;
    const draggableRowsHtml = this.cutDescendentRows(folderId, newParentId);
    const $tree = $(this.treeTarget);
    $tree.treetable("removeNode", folderId);
    this.removeFolderIconIfEmpty(parentFolderId);
    const parentNode = $tree.treetable("node", newParentId);
    $tree.treetable("loadBranch", parentNode, draggableRowsHtml);

    // restore event handlers for moved nodes
    this.makeTreeNodesDraggable();
  }

  cutDescendentRows(folderId, newParentId) {
    let html = "";
    let cutFolders = [];
    this.treeTarget.querySelectorAll("tr[data-tt-id]").forEach((row) => {
      if (
        row.dataset.ttId == folderId ||
        cutFolders.includes(row.dataset.ttParentId)
      ) {
        row.querySelector(".indenter").remove();
        if (row.dataset.ttId == folderId) {
          row.dataset.ttParentId = newParentId;
        }
        html += row.outerHTML;
        cutFolders.push(row.dataset.ttId);
      }
    });
    return html;
  }

  removeFolderIconIfEmpty(folderId) {
    const node = $(this.treeTarget).treetable("node", folderId);
    if (node.children.length == 0) {
      const folderIcon = node.row[0].querySelector("span.indenter > a");
      if (folderIcon) {
        folderIcon.remove();
      }
    }
  }

  activateDocumentDropTargets(state) {
    if (this.hasDroppableClass) {
      this.element.classList.toggle(this.droppableClass, state);
    }
  }

  activateDocumentDropTarget(rowElement, state) {
    if (this.hasDropOverClass) {
      rowElement.classList.toggle(this.dropOverClass, state);
    }
  }

  async addDocumentToFolder(folderId, documentId, documentType) {
    return await this.store.addDocumentToFolder(
      folderId,
      this.folderName(folderId),
      documentId,
      documentType,
    );
  }

  folderName(folderId) {
    const rowElement = this.folderTargets.find(
      (rowElement) => rowElement.dataset.ttId == folderId,
    );
    if (rowElement) {
      return rowElement.querySelector(".folders-row__folder-name").textContent;
    }
  }
}
